import type { ImportModelToMutate } from '@nx/api';
import { StoreKit, addNotification } from '@nx/state';
import { isNullish } from '@nx/stdlib';
import { addListener, createListenerMiddleware } from '@reduxjs/toolkit';
import type { ListenerMiddlewareInstance, TypedAddListener, TypedStartListening } from '@reduxjs/toolkit';
import { debounce } from 'lodash-es';

import { APP_VERSION } from '../../constants';
import { IMPORT_MODEL_SAVE_TO_STORAGE_FAILED } from '../../constants/notification';
import { dehydrateVisualisationState } from '../../visualisation/utils';
import { sharedStorageSlice } from '../apis/storage';
import type { AppDispatch, RootState } from '../store';

const SAVE_MODEL_DEBOUNCE = 1000;

export const modelSaveMiddleware: ListenerMiddlewareInstance<RootState> = createListenerMiddleware<RootState>();

export const startAppListening: TypedStartListening<RootState, AppDispatch> =
  modelSaveMiddleware.startListening.withTypes<RootState, AppDispatch>();

export const addAppListener: TypedAddListener<RootState, AppDispatch> = addListener.withTypes<RootState, AppDispatch>();

const debouncedSaveModelToSharedStorage = debounce(
  async (graphModelState: ImportModelToMutate, importModelId: string, dispatch: AppDispatch) => {
    try {
      await dispatch(
        sharedStorageSlice.endpoints.updateImportModel.initiate({
          id: importModelId,
          body: graphModelState,
        }),
      ).unwrap();
    } catch (error) {
      console.error('Failed to save model:', error);
      const { globalStore } = StoreKit.UNSAFE_getGlobalStoreInstance();
      globalStore.dispatch(addNotification(IMPORT_MODEL_SAVE_TO_STORAGE_FAILED));
    }
  },
  SAVE_MODEL_DEBOUNCE,
);

// Save model to shared storage, after all actions that modify the model, debounced
startAppListening({
  predicate: (action) =>
    (action.type.startsWith('dataModel/') && !action.type.startsWith('dataModel/initialise')) ||
    (action.type.startsWith('visualisation/') && !action.type.startsWith('visualisation/initialise')) ||
    action.type.startsWith('model/') ||
    // React on when data source id is changed
    action.type.startsWith('configurations/setDataSourceId') ||
    // React on when model name is changed
    action.type.startsWith('configurations/setImportModelName'),
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState();
    const { importModelId, importModelName, dataSourceId } = state.configurations;

    if (isNullish(importModelId)) {
      return;
    }

    const currentState = listenerApi.getState();
    const { dataModel, visualisation } = currentState;

    const graphModelState: ImportModelToMutate = {
      dataModel: JSON.stringify(dataModel),
      visualisation: JSON.stringify(dehydrateVisualisationState(visualisation)),
      version: APP_VERSION,
      name: importModelName,
      dataSourceId,
    };

    const { dispatch } = listenerApi;

    await debouncedSaveModelToSharedStorage(graphModelState, importModelId, dispatch);
  },
});
